import React, {createContext, useContext, Fragment, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';

interface IActionModalProviderProps {}

interface IActionModalContext {
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  setContent: React.Dispatch<React.SetStateAction<string>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOnClickAction: React.Dispatch<React.SetStateAction<() => void>>;
  setIsAlert: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ActionModalContext = createContext<IActionModalContext>(null);
export const useActionModal = (): IActionModalContext => useContext(ActionModalContext);

const ActionModalProvider: React.FunctionComponent<IActionModalProviderProps> = ({children}) => {
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [onClickAction, setOnClickAction] = useState<() => void>(null);
  const [isAlert, setIsAlert] = useState<boolean>(false);

  const completeButtonRef = useRef(null);
  const closeModal = () => setIsOpen(false);

  const handleOnClickAction = () => {
    closeModal();
    onClickAction();
  };

  return (
    <ActionModalContext.Provider
      value={{
        setTitle,
        setContent,
        setIsOpen,
        setIsAlert,
        setOnClickAction,
      }}>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" initialFocus={completeButtonRef} className="fixed inset-0 z-10 overflow-y-auto" onClose={closeModal}>
          <div className="fixed inset-0 opacity-50 bg-black"></div>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Dialog.Overlay className="fixed inset-0" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <Dialog.Title as="h3" className={`text-lg font-medium leading-6 ${isAlert ? 'text-red-600' : 'text-green-600'}`}>
                  {title}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{content}</p>
                </div>

                <div className="flex items-center mt-6">
                  <div className="mr-4">
                    <button
                      type="button"
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-900 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      onClick={closeModal}>
                      Cancel
                    </button>
                  </div>

                  <div>
                    <button
                      type="button"
                      ref={completeButtonRef}
                      className={`inline-flex justify-center px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                        isAlert ? 'text-red-900 bg-red-100 hover:bg-red-200' : 'text-green-900 bg-green-100 hover:bg-green-200'
                      }`}
                      onClick={handleOnClickAction}>
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      {children}
    </ActionModalContext.Provider>
  );
};

export default ActionModalProvider;
